import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/contact#webpage`,
      url: `${SITE_URL}/contact`,
      name: 'お問い合わせ｜岡山市の工務店・リフォーム｜株式会社おかやま工務店',
      description:
        '株式会社おかやま工務店へのお問い合わせページ。外装工事・内装工事・リフォーム・メンテナンスのご相談はこちら。見積もり無料・即日対応可能。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      dateModified: new Date().toISOString().split('T')[0],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2'],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'お問い合わせ', item: `${SITE_URL}/contact` },
        ],
      },
      mainEntity: {
        '@type': 'ContactPoint',
        telephone: '086-012-3456',
        contactType: 'customer service',
        areaServed: 'JP',
        availableLanguage: 'Japanese',
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '17:00',
        },
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-contact');
      if (el) el.remove();
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    phone: '',
    email: '',
    postalCode: '',
    address: '',
    inquiryType: '',
    message: '',
    agree: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agree) {
      alert('個人情報の取り扱いに同意してください');
      return;
    }

    if (formData.message.length > 500) {
      alert('ご相談内容は500文字以内で入力してください');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('お名前', formData.name);
      formBody.append('フリガナ', formData.furigana);
      formBody.append('電話番号', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('郵便番号', formData.postalCode);
      formBody.append('ご住所', formData.address);
      formBody.append('お問い合わせ内容', formData.inquiryType);
      formBody.append('ご相談内容', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6juhafgl4s4nd8dm4j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          furigana: '',
          phone: '',
          email: '',
          postalCode: '',
          address: '',
          inquiryType: '',
          message: '',
          agree: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 px-6 bg-gradient-to-br from-blue-50 to-blue-100 mt-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1a1a2e] leading-tight mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お問い合わせ
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed">
            お気軽にご相談ください
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="山田太郎"
                  />
                </div>

                {/* Furigana */}
                <div>
                  <label htmlFor="furigana" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    フリガナ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="furigana"
                    name="furigana"
                    value={formData.furigana}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="ヤマダタロウ"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Postal Code */}
                <div>
                  <label htmlFor="postalCode" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    郵便番号
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="700-0000"
                  />
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    ご住所
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="岡山市北区○○町1-2-3"
                  />
                </div>

                {/* Inquiry Type */}
                <div>
                  <label htmlFor="inquiryType" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="外装工事について">外装工事について</option>
                    <option value="内装工事について">内装工事について</option>
                    <option value="リフォームについて">リフォームについて</option>
                    <option value="メンテナンスについて">メンテナンスについて</option>
                    <option value="見積もり依頼">見積もり依頼</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    ご相談内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm resize-none"
                    placeholder="ご相談内容を詳しくお書きください（500文字以内）"
                  ></textarea>
                  <p className="text-xs text-gray-600 mt-1 text-right">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {/* Agreement */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                    required
                    className="mt-1 w-5 h-5 text-[#ff6b35] border-gray-300 rounded focus:ring-[#ff6b35] cursor-pointer"
                  />
                  <label htmlFor="agree" className="ml-3 text-sm text-gray-700 cursor-pointer">
                    個人情報の取り扱いに同意する <span className="text-red-500">*</span>
                  </label>
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-[#ff6b35] text-white text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                    <p className="text-green-700 font-bold text-center">
                      お問い合わせありがとうございます。<br />
                      内容を確認次第、ご連絡させていただきます。
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                    <p className="text-red-700 font-bold text-center">
                      送信に失敗しました。<br />
                      お手数ですが、お電話にてお問い合わせください。
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info Card */}
            <div className="lg:col-span-1">
              <div className="bg-[#1a1a2e] rounded-2xl p-8 text-white sticky top-24">
                <h3 className="text-2xl font-bold mb-6">お急ぎの方はお電話で</h3>
                <a
                  href="tel:086-012-3456"
                  className="block text-4xl font-black mb-6 hover:text-[#ff6b35] transition-colors"
                >
                  086-012-3456
                </a>
                <div className="space-y-3 text-sm text-gray-300">
                  <p className="flex items-start">
                    <i className="ri-time-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>営業時間: 8:00 〜 17:00</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-calendar-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>定休日: 日曜日・祝日</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-map-pin-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>岡山市北区○○町1-2-3</span>
                  </p>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-700">
                  <p className="text-sm leading-relaxed">
                    お見積もりは無料です。<br />
                    どんな小さなことでも<br />
                    お気軽にご相談ください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;